/**
 * **Les mots des assists** — ce qu'un paramètre d'assist veut dire pour son mini-jeu, en
 * français, avec son chiffre (SPEC-UI §3.5 « assist d'Atelier : icône du mini-jeu assisté +
 * effet chiffré », §3.3 « assists actifs listés avec leur icône »).
 *
 * ⚠️ **Ce fichier NOMME, il ne calcule rien.** La sémantique de chaque paramètre est celle de
 * `server/src/atelier/assists.ts` — le seul endroit qui traduit un assist en option de pattern,
 * re-mapping ⟦A-3⟧ compris (`fenetres_pct` est devenu la zone des tiers, `retakes_gratuites` des
 * incidents en moins…), puis ⟦C53 · A-170 §2⟧ : la zone verte de « La prise parfaite », des prises
 * ratées effacées, la première prise assurée, un plateau éclairé. Les libellés ci-dessous en sont la lecture, ligne par ligne : si le
 * serveur change un sens, c'est ici qu'il faut le redire. Les DEGRÉS (les valeurs) viennent
 * du contenu et s'affichent tels quels — les régler est de l'équilibrage, donc pas d'ici.
 */

import { PRISE_INTRO_PLANCHER, type AssistParam, type AtelierStage, type MinigameId } from "@maxime/shared";

/** Les cinq mini-jeux : icône de l'étape, nom du jeu, étape du moteur. */
export const MINIJEUX: Record<MinigameId, { icone: string; nom: string; etape: AtelierStage }> = {
  fil_rouge: { icone: "✍️", nom: "Le script qui tombe", etape: "ecriture" },
  bonne_prise: { icone: "🎥", nom: "La prise parfaite", etape: "tournage" },
  couper_rythme: { icone: "✂️", nom: "Couper dans le rythme", etape: "montage" },
  putaclic_o_metre: { icone: "🖼️", nom: "Le putaclic-o-mètre", etape: "miniature" },
  la_decoupe: { icone: "📣", nom: "La Découpe", etape: "promotion" },
};

/** `+20 %` / `−10 %` — un pourcentage signé, à la française. */
function pct(v: number): string {
  return `${v > 0 ? "+" : "−"}${Math.abs(v)} %`;
}

/**
 * L'effet chiffré d'un assist, tel que le joueur doit le lire à l'intro d'étape et dans l'arbre.
 * `value` est la valeur **du contenu** (un nœud) ou **agrégée** (tous les nœuds possédés) — le
 * texte est le même, c'est l'appelant qui sait ce qu'il additionne.
 */
export function libelleAssist(param: AssistParam, value: number): string {
  switch (param) {
    // — Écriture —
    // ⟦C52 · A-170⟧ Re-mappés sur le jeu de mots qui tombent (même enum fermé, autre geste).
    case "beats_preplaces":
      return `${value} bloc${value > 1 ? "s" : ""} Histoire déjà dans la bonne colonne`;
    case "hook_toujours_premier":
      return "le premier bloc est une Accroche déjà rangée";
    case "erreurs_pardonnees":
      return `${value} erreur${value > 1 ? "s" : ""} pardonnée${value > 1 ? "s" : ""} au barème`;
    case "chrono_bonus_pct":
      return `les blocs tombent ${Math.abs(value)} % plus lentement`;
    // — Tournage (re-mapping ⟦A-3⟧, puis ⟦C53 · A-170 §2⟧ : une jauge de jeu d'acteur) —
    case "fenetres_pct":
      return `zone verte ${pct(value)}`;
    case "retakes_gratuites":
      return `${value} prise${value > 1 ? "s" : ""} ratée${value > 1 ? "s" : ""} effacée${value > 1 ? "s" : ""} de la moyenne`;
    case "premiere_fenetre_reussie":
      return `la première prise est assurée (${PRISE_INTRO_PLANCHER} au minimum)`;
    case "phase_lumieres":
      return "plateau éclairé : la zone verte s'élargit";
    // — Montage —
    case "fenetre_rythmique_pct":
      return `fenêtre rythmique ${pct(value)}`;
    case "piste_beat_visible":
      return "la piste s'affiche plus tôt";
    case "tempo_percu_pct":
      return `tempo perçu −${Math.abs(value)} %`;
    case "phase_reperage":
      return "un geste de plus : le repérage avant la piste";
    // — Miniature (re-mapping ⟦A-3.1⟧) —
    case "zone_accroche_pct":
      return `sweet spot ${pct(value)}`;
    case "curseur_ralenti_pct":
      return `aiguille moins bruitée (−${Math.abs(value)} %)`;
    case "relances_gratuites":
      return "amortisseur : le tremblement de l'aiguille est divisé par deux";
    case "phase_promesse":
      return "un geste de plus : la promesse avant le dosage";
    // — Promotion —
    case "heatmap_revelee_pct":
      return `${Math.abs(value)} % des zones chaudes révélées d'entrée`;
    case "clips_bonus":
      return "un extrait de plus à poser";
    case "clips_cout_energie_zero":
      return "poser un extrait ne coûte plus d'énergie";
    case "zones_clignotent":
      return "toutes les zones chaudes clignotent";
    case "phase_metrage":
      return "un geste de plus : le métrage avant la découpe";
  }
}

/** Un assist de contenu, lisible d'un coup : « 🎥 La prise parfaite, zone verte +10 % ». */
export function decrireAssist(assist: {
  minigame: MinigameId;
  param: AssistParam;
  value: number;
}): { icone: string; jeu: string; texte: string } {
  const jeu = MINIJEUX[assist.minigame];
  return { icone: jeu.icone, jeu: jeu.nom, texte: libelleAssist(assist.param, assist.value) };
}
